// src/components/Sidebar.tsx
import { NavLink } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Ion from './Ion'; // wrapper ligero para <ion-icon>
import { useAcl, A } from '../auth/acl'; // ⬅️ si tu acl.ts está en src/acl.ts usa: ../acl

const SIDEBAR_WIDTH = 260;
const SIDEBAR_WIDTH_COLLAPSED = 74;

type MenuItem = {
  to: string;
  icon: string;           // nombre Ionicon (home-outline, people-outline, etc.)
  label: string;
  ability?: keyof typeof A; // clave de habilidad en A (opcional)
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  // ACL (trae can(), me, roles, etc.)
  const acl = useAcl(); // { can: (ability) => boolean, me?: { first_name, last_name, roles?: string[] } ... }

  // Datos del usuario (fallback: localStorage si ACL no expone me)
  const { me, roles } = useMemo(() => {
    if (acl && (acl as any).me) {
      const u = (acl as any).me as { first_name?: string; last_name?: string; roles?: string[] };
      return { me: u, roles: u?.roles ?? [] };
    }
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null') as
        | { first_name?: string; last_name?: string; roles?: string[] }
        | null;
      return { me: u || undefined, roles: u?.roles ?? [] };
    } catch {
      return { me: undefined, roles: [] };
    }
  }, [acl]);

  const displayName = useMemo(() => {
    const n = `${me?.first_name ?? ''} ${me?.last_name ?? ''}`.trim();
    return n || 'Usuario';
  }, [me]);

  const roleBadge = useMemo(() => {
    if (!roles?.length) return 'Sin rol';
    return roles.length === 1 ? roles[0] : `${roles[0]} +${roles.length - 1}`;
  }, [roles]);

  // Variables de ancho del sidebar
  useEffect(() => {
    document.body.style.setProperty('--sidebar-w', `${SIDEBAR_WIDTH}px`);
    document.body.style.setProperty('--sidebar-w-collapsed', `${SIDEBAR_WIDTH_COLLAPSED}px`);
    return () => {
      document.body.style.removeProperty('--sidebar-w');
      document.body.style.removeProperty('--sidebar-w-collapsed');
    };
  }, []);

  // Clase en <body> para modo comprimido
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
  }, [collapsed]);

  // Menú (definición completa)
  const rawMenu: MenuItem[] = useMemo(
    () => [
      { to: '/devioz/dashboard', icon: 'bar-chart-outline',        label: 'Dashboard', ability: 'ReportsView' },
      { to: '/devioz/inicio',     icon: 'home-outline',             label: 'Inicio' },
      { to: '/devioz/empresas',   icon: 'briefcase-outline',        label: 'Empresas', ability: 'CompaniesView' },
      { to: '/devioz/usuarios',   icon: 'people-outline',           label: 'Usuarios', ability: 'UsersView' },
      { to: '/devioz/roles',      icon: 'shield-checkmark-outline', label: 'Roles',    ability: 'RolesView' },
    ],
    []
  );

  // Filtro por ACL
  const menu = useMemo(() => {
    const can = (acl && (acl as any).can) as ((ab: any) => boolean) | undefined;
    return rawMenu.filter((m) => {
      if (!m.ability) return true;              // accesible sin permiso explícito
      if (!A || !A[m.ability]) return false;    // habilidad desconocida -> ocultar
      return typeof can === 'function' ? can(A[m.ability]) : true; // si no hay can(), no filtra
    });
  }, [rawMenu, acl]);

  return (
    <aside className="app-sidebar">
      {/* Perfil */}
      <div className="nav-profile">
        <div className="avatar" />
        <div className="profile-text">
          <div className="profile-name">{displayName}</div>
          <div className="profile-role">{roleBadge}</div>
        </div>
      </div>

      <div className="nav-title">NAVEGACIÓN</div>

      {/* Navegación */}
      <nav className="sidebar-nav">
        {menu.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            <Ion name={m.icon} className="nav-ico" />
            <span className="menu-text">{m.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Pie de barra: copy + botón pin */}
      <footer className="sidebar-footer">
        <span className="sidebar-copy">© 2025 Devioz</span>

        <button
          type="button"
          className="sidebar-pin"
          aria-label={collapsed ? 'Expandir barra' : 'Contraer barra'}
          onClick={() => setCollapsed((v) => !v)}
        >
          <Ion name={collapsed ? 'chevron-forward' : 'chevron-back'} className="pin-ico" />
        </button>
      </footer>
    </aside>
  );
}
